import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Radio } from "lucide-react";
import UserNameLink from "./UserNameLink";

/**
 * Who's Online — visible only to admin + moderator. Polls /api/admin/presence
 * every 20s. Presence is driven by `users.last_seen_at`, updated on every
 * authenticated API call (throttled to 1 write per user per 60s in core.py).
 */
export default function OnlineUsersCard() {
  const [data, setData] = useState({ online: 0, idle: 0, total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/presence");
      setData(data);
    } catch { /* silently swallow — non-staff hits will 403 */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 20_000);
    return () => clearInterval(timerRef.current);
  }, []);

  const relTime = (secondsAgo) => {
    if (secondsAgo == null) return "";
    if (secondsAgo < 60) return `${secondsAgo}s`;
    if (secondsAgo < 3600) return `${Math.floor(secondsAgo / 60)}m`;
    return `${Math.floor(secondsAgo / 3600)}h`;
  };

  const items = data.items || [];
  const online = useMemo(() => items.filter((u) => u.status === "online"), [items]);
  const idle = useMemo(() => items.filter((u) => u.status === "idle"), [items]);

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-5" data-testid="online-users-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Presence</div>
          <div className="font-display font-black uppercase tracking-tight text-lg text-slate-900 flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            Who&#39;s Online
          </div>
        </div>
        <div className="text-right text-[11px] font-mono-num text-slate-600 tabular-nums leading-tight">
          <div><span className="text-emerald-400 font-semibold" data-testid="presence-online-count">{data.online}</span> online</div>
          <div><span className="text-slate-800" data-testid="presence-idle-count">{data.idle}</span> idle</div>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400 font-mono-num text-xs">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-sm font-mono-num text-center py-4 border border-dashed border-slate-200">
          No one active in the last 30 minutes.
        </div>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto" data-testid="presence-list">
          {[...online, ...idle].slice(0, 30).map((u) => (
            <li
              key={u.id}
              className="flex items-center gap-3 py-1.5 px-2 border border-transparent hover:border-slate-200 transition-colors"
              data-testid={`presence-item-${u.id}`}
            >
              <span className={`relative flex h-2 w-2 shrink-0`}>
                {u.status === "online" && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                )}
                <span className={`relative inline-flex rounded-full h-2 w-2 ${u.status === "online" ? "bg-emerald-500" : "bg-amber-500"}`} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-slate-900 text-sm truncate flex items-center gap-2">
                  <UserNameLink
                    userId={u.id}
                    handle={u.handle}
                    name={u.name}
                    className="text-slate-900"
                    showUnderline={false}
                    testid={`presence-name-link-${u.id}`}
                  />
                  {u.role === "admin" && (
                    <span className="font-mono-num text-[9px] uppercase tracking-widest border border-red-500/50 text-red-400 px-1">admin</span>
                  )}
                  {u.role === "moderator" && (
                    <span className="font-mono-num text-[9px] uppercase tracking-widest border border-[#0D9488]/50 text-[#0D9488] px-1">mod</span>
                  )}
                  {u.has_premium && <span className="text-[#0D9488] text-xs">★</span>}
                </div>
                <div className="text-[10px] font-mono-num text-slate-500 truncate">{u.email}</div>
              </div>
              <div className="text-[10px] font-mono-num text-slate-500 tabular-nums shrink-0">
                {relTime(u.seconds_ago)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
